import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from "@angular/core";
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  Validators,
} from "@angular/forms";
import { NgbDateParserFormatter } from "@ng-bootstrap/ng-bootstrap";
import { PlacementArray } from "@ng-bootstrap/ng-bootstrap/util/positioning";
import { IUser } from "app/auth/user.model";
import { NgbCustomParserFormatter } from "components/common/date-picker/ngb-custom-parser-formatter";
import { ContactDetails } from "pages/company-resources/contact-details/contact-details";
import { commonVar } from "providers/common-var/common";
import { ResourceManagementService } from "providers/resource-management/resource-management.service";
import { AccessControlService } from "../../../../app/access-control/access-control-service";
import { Workforce } from "../../../../app/access-control/permissions";
import { PermissionsResponse } from "../../../../app/access-control/permissions-response";
import ResourceManagementLookups from "../../../../models/common/resource-management-lookups";
import Company from "../../../../models/company-details/company";
import CompanyLocation from "../../../../models/company-details/company-location";
import LocationDto from "../../../../models/company-resources/location-dto";
import {
  OrgSubType,
  orgSubTypeLabel,
  ORG_SUB_TYPES,
  parseOrgSubType,
} from "../../../../models/company-resources/org-sub-type";
import PhoneControl from "../../../../models/company-resources/phone-control";
import { BusinessPhoneNumberType } from "../../../../models/contact/business-phone-number";
import SortOptions from "../../../../models/content-wrapper/sort-options";
import { CompanyManagementService } from "../../../../providers/company-management-service/company-management-service";
import { UtilityService } from "../../../../providers/utility-service/utility-service";

const ENTITY_TYPE_COMPANY = "COMPANY";
const ENTITY_TYPE_LOCATION = "LOCATION";
// OPS-2858: explicit, because the server default is 20. ~100 locations exist
// system-wide, so this comfortably covers the widened support-user picker.
const LOCATION_FETCH_SIZE = 1000;
const COMPANY_FETCH_SIZE = 300;

// OPS-2858 support-user defaults. Values mirror the resource-management enums:
// ContactType.NON_PROVIDER_CLINICIAN(4), ProfessionalRelation.EMPLOYEE(2),
// ResourceStatus.ACTIVE(1), AddressType.OFFICE(3).
const SUPPORT_USER_RESOURCE_TYPE = 4;
const SUPPORT_USER_PROFESSIONAL_RELATION = 2;
const RESOURCE_STATUS_ACTIVE = 1;
const ADDRESS_TYPE_OFFICE = 3;
// The Curantis office. A support user is internal staff, so their "address" is ours -
// it carries no meaning, it just has to be something.
const SUPPORT_USER_DEFAULT_ADDRESS = {
  address1: "3939 Belt Line Road",
  city: "Addison",
  state: "TX",
  zip: "75001",
};
// REQUIRED, and not by validation: MappingUtilities:358 does
// dto.getPhoneNumbers().stream().findFirst().get() with no guard, so an empty phone
// list throws NoSuchElementException and the save 500s. Every other phone access in
// that file checks CollectionUtils.isEmpty first; that one does not.
const SUPPORT_USER_DEFAULT_WORK_PHONE = "5555555555";

@Component({
  selector: "workforce-general-contact",
  templateUrl: "workforce-general-contact.html",
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => WorkforceGeneralContact),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => WorkforceGeneralContact),
      multi: true,
    },
    { provide: NgbDateParserFormatter, useClass: NgbCustomParserFormatter },
  ],
  changeDetection: ChangeDetectionStrategy.Default,
})
export class WorkforceGeneralContact
  extends ContactDetails
  implements OnInit, OnDestroy, AfterViewInit
{
  @Input() company: Company | undefined;
  @Input() user: IUser | undefined;
  @Input() isVolunteer: boolean;

  /**
   * OPS-2858. Support Users tab only. Widens the picker from "this company's
   * locations" to every company and location system-wide, and pins the primary org
   * to the company. The other three contact types are untouched.
   */
  @Input() isSupportUser: boolean = false;

  /**
   * OPS-2858. Company rows for the support-user picker, each with its locations.
   * A COMPANY is itself a grantable org - iam-functions matches OrgRole.OrgID ==
   * tenantId with NO hierarchy - so `org` here is a real selectable option, not a
   * group header. Granting the company does not confer its locations, and granting
   * every location does not confer the company.
   */
  orgTree: { org: LocationDto; locations: LocationDto[] }[] = [];
  tempFormValue = null;
  characterCount: number = 0;

  resourceTypeOptions = [];
  professionalRelationOptions = [];
  statusOptions = [];
  addressTypeOptions = [];
  genderOptions = [];
  phoneNumberOptions = [];
  languageOptions = [];
  suffixOptions = [];
  allAddressTypeOptions = [];
  allPhoneNumberOptions = [];
  private request: any = {};
  primaryLocations: LocationDto[] = [];
  primaryLocationSelected: LocationDto;
  otherLocations: LocationDto[] = [];
  otherLocationsSelected: LocationDto[] = [];

  phoneNumbersControls: PhoneControl[] = [
    {
      description: "Work",
      control: "workPhoneNumbers",
      phoneType: BusinessPhoneNumberType.WORK,
    },
    {
      description: "Cell",
      control: "cellPhoneNumbers",
      phoneType: BusinessPhoneNumberType.CELL,
    },
    {
      description: "Fax",
      control: "faxPhoneNumbers",
      phoneType: BusinessPhoneNumberType.FAX,
    },
  ];

  private sortOptions: SortOptions = new SortOptions("name", "asc");
  _volunteerAttributesAdded: boolean = false;
  datePlacementPreference: PlacementArray = ["bottom-left"];
  _contactIsEmployee: boolean = false;
  _contactIsVolunteer: boolean = false;
  _isAdministrator: boolean = false;
  _contactIsSystemUser: boolean = false;
  dateOfBirthMin: any = "";
  dateOfBirthMax: any = "";
  driversLicenseExpMin: any = "";
  driversLicenseExpMax: any = "";
  hireDateMin: any = "";
  hireDateMax: any = "";

  emailRegex = commonVar.EMAIL_REGEX;
  phoneMask = commonVar.PHONE_US_MASK;
  zipCodeMask = commonVar.ZIP_CODE_MASK;
  zipCodePattern = commonVar.ZIP_PATTERN;
  zipExtensionPattern = commonVar.ZIP_EXT_PATTERN;
  zipExtensionMask = commonVar.ZIP_CODE_EXTENSION_MASK;
  stateList = commonVar.STATE_LIST;
  sanitizedInput = commonVar.SANITIZIED_INPUT_REGEX;
  protected readonly Workforce = Workforce;
  currentEmailAddress: string;
  @Input() isEditMode: boolean = false;
  _isNew = false;
  @Output() deleteAddress = new EventEmitter<any>();
  private _rmLookups: ResourceManagementLookups;
  _permissionsAccess = {} as PermissionsResponse;
  hasAccessToAccountSetup: boolean = false;

  @Input()
  set permissionsAccess(permissionsAccess: PermissionsResponse) {
    if (
      permissionsAccess &&
      permissionsAccess.permissions &&
      permissionsAccess.permissions.length > 0
    ) {
      this._permissionsAccess = permissionsAccess;
      this.checkCognitoUserPermissions();
    }
  }

  @Input()
  set rmLookups(rmLookups: ResourceManagementLookups) {
    this._rmLookups = rmLookups;
    if (rmLookups && rmLookups.Position && rmLookups.Position.length > 0) {
      this.mapLookups(rmLookups);
    }
  }

  get rmLookups() {
    return this._rmLookups;
  }

  @Input() set isNew(value: boolean) {
    this._isNew = !!value;
  }

  get isNew(): boolean {
    return this._isNew;
  }

  @Input() set isAdministrator(value: boolean) {
    this._isAdministrator = value;
  }

  get isAdministrator() {
    return this._isAdministrator;
  }

  @Input() set contactIsSystemUser(value: boolean) {
    this._contactIsSystemUser = value;
  }

  get contactIsSystemUser() {
    return this._contactIsSystemUser;
  }

  get addresses() {
    return this.innerFormGroup.get("addresses") as FormArray;
  }

  get phoneNumbers() {
    return this.innerFormGroup.get("phoneNumbers") as FormArray;
  }

  get workPhoneNumbers() {
    return this.innerFormGroup.get("workPhoneNumbers") as FormArray;
  }

  get faxPhoneNumbers() {
    return this.innerFormGroup.get("faxPhoneNumbers") as FormArray;
  }

  get cellPhoneNumbers() {
    return this.innerFormGroup.get("cellPhoneNumbers") as FormArray;
  }

  get deletedPhoneNumbers() {
    return this.innerFormGroup.get("deletedPhoneNumbers") as FormArray;
  }

  get professionalRelation() {
    return this.innerFormGroup.get("professionalRelation") as FormControl;
  }

  get resourceType() {
    return this.innerFormGroup.get("resourceType") as FormControl;
  }

  get groupAffiliation() {
    return this.innerFormGroup.get("groupAffiliation") as FormControl;
  }

  get notes() {
    return this.innerFormGroup.get("notes") as FormControl;
  }

  get dateOfBirth() {
    return this.innerFormGroup.get("dateOfBirth") as FormControl;
  }

  get gender() {
    return this.innerFormGroup.get("gender") as FormControl;
  }

  get suffix() {
    return this.innerFormGroup.get("suffix") as FormControl;
  }

  get primaryLanguage() {
    return this.innerFormGroup.get("primaryLanguage") as FormControl;
  }

  get emailAddress() {
    return this.innerFormGroup.get("emailAddress") as FormControl;
  }

  get systemUser() {
    return this.innerFormGroup.get("systemUser") as FormControl;
  }

  get contactIsEmployee() {
    return this._contactIsEmployee;
  }

  set contactIsEmployee(value: boolean) {
    this._contactIsEmployee = value;
    this.setContactInputs();
  }

  set volunteerAttributesAdded(value: boolean) {
    this._volunteerAttributesAdded = value;
  }

  get volunteerAttributesAdded() {
    return this._volunteerAttributesAdded;
  }

  get contactIsVolunteer() {
    return this._contactIsVolunteer;
  }

  set contactIsVolunteer(value: boolean) {
    this._contactIsVolunteer = value;
    this.setContactInputs();
  }

  set value(value: any) {
    if (value.phoneNumbers && value.phoneNumbers.length > 0) {
      const phoneNumbersCount = value.phoneNumbers.length;
      let phoneNumbersControlsCount = this.phoneNumbers.controls.length;
      if (phoneNumbersControlsCount === 0 && phoneNumbersCount > 0) {
        value.phoneNumbers.forEach((phoneNumber) => {
          this.addPhoneNumber(phoneNumber.phoneNumberType);
        });
      }
    }

    // CO-3511: this used to read `if (value.addresses && value.length > 0)`.
    // `value` is the plain resource object, so `value.length` is undefined and the
    // whole block was unreachable — the FormArray never grew past the single row
    // added in the constructor. A resource with 2+ addresses therefore rendered one
    // address AND round-tripped one address back to the API, silently deleting the
    // others. (The component this was copied from, general-contact-details.ts, has
    // the correct `if (value.addresses)`.)
    //
    // Sized rather than appended-to because loadData() patches `generalDetails`
    // twice in a row, so writeValue lands twice per edit and a naive add-per-extra
    // would double the rows.
    if (value.addresses) {
      value.addresses.forEach((addressCtrl) => {
        if (addressCtrl && typeof addressCtrl.zipExtension === "string") {
          addressCtrl.zipExtension = addressCtrl.zipExtension.trim();
        }
      });

      // CO-3511: the constructor adds one blank address row ("initialize to 1 for
      // new users") and EVERY field on it is required — but it runs for edits too.
      // So opening an existing resource that has no address left a blank required
      // row behind, the form was invalid the moment it painted, and Save was
      // disabled forever: that record could never be saved by anyone. Verified on
      // dev, where the first provider reports exactly those five address controls
      // invalid and its Save never enables.
      //
      // Size the array to the payload, down to ZERO for an existing resource. A NEW
      // resource still keeps one blank row so there is somewhere to type.
      const target = Math.max(value.addresses.length, this.isNew ? 1 : 0);

      while (this.addresses.length < target) {
        this.addAddress();
      }
      while (this.addresses.length > target) {
        this.addresses.removeAt(this.addresses.length - 1);
      }
    }
    this.tempFormValue = value;
  }

  constructor(
    protected formBuilder: FormBuilder,
    private resourceManagementService: ResourceManagementService,
    private utilityService: UtilityService,
    protected companyManagementService: CompanyManagementService,
    private accessCntrl: AccessControlService
  ) {
    super(formBuilder);

    this.innerFormGroup = this.formBuilder.group({
      resourceType: [null, Validators.required],
      professionalRelation: [null, Validators.required],
      employedBy: [""],
      resourceStatus: [null, Validators.required],
      effectiveDate: [""],
      systemUser: [false],
      firstName: [
        "",
        [Validators.required, Validators.pattern(this.sanitizedInput)],
      ],
      middleInitial: ["", Validators.pattern(this.sanitizedInput)],
      lastName: [
        "",
        [Validators.required, Validators.pattern(this.sanitizedInput)],
      ],
      suffix: [""],
      preferredName: [""],
      gender: [null],
      emailAddress: [
        "",
        [Validators.pattern(this.emailRegex), Validators.maxLength(50)],
      ],
      groupAffiliation: [""],
      phoneNumbers: new FormArray([]),
      workPhoneNumbers: this.formBuilder.array([]),
      faxPhoneNumbers: new FormArray([]),
      cellPhoneNumbers: new FormArray([]),
      deletedPhoneNumbers: new FormArray([]),
      addresses: new FormArray([]),
      notes: [""],
      location: [null, Validators.required],
      otherLocations: [null],
      companyId: [null, Validators.required],
      companyName: [null, Validators.required],
    });
    // initialize to 1 for new users
    this.addAddress();

    let now = new Date();
    const today = {
      year: now.getFullYear(),
      month: now.getMonth() + 1,
      day: now.getDate(),
    };
    const tenYearsLater = {
      year: now.getFullYear() + 10,
      month: now.getMonth() + 1,
      day: now.getDate(),
    };
    const eightyYearsAgo = {
      year: now.getFullYear() - 80,
      month: now.getMonth() + 1,
      day: now.getDate(),
    };

    this.dateOfBirthMin = { year: 1900, month: 1, day: 1 };
    this.dateOfBirthMax = today;
    this.driversLicenseExpMin = today;
    this.driversLicenseExpMax = tenYearsLater;
    this.hireDateMin = eightyYearsAgo;
    this.hireDateMax = tenYearsLater;

    this.addSubscriptions();
  }

  private addSubscriptions() {
    this.subscriptions.push(
      this.innerFormGroup.valueChanges.subscribe((value) => {
        this.onChange(value);
        this.onTouched();
      })
    );

    this.subscriptions.push(
      this.professionalRelation.valueChanges.subscribe((value) => {
        this.contactIsEmployee = value === 2;
      })
    );

    this.subscriptions.push(
      this.resourceType.valueChanges.subscribe((value) => {
        this.contactIsVolunteer = value === 5;
      })
    );

    this.subscriptions.push(
      this.notes.valueChanges.subscribe((value) => {
        this.characterCount = value ? value.length : 0;
      })
    );

    this.subscriptions.push(
      this.emailAddress.valueChanges.subscribe((value) => {
        this.resourceManagementService.changeEmailAddress(value);
      })
    );
  }

  ngOnInit() {
    // CO-3511: this subscription was not tracked, so ngOnDestroy could not tear it
    // down. systemUserEmailAddress is a long-lived subject on a singleton provider,
    // so every visit to the edit page left another live callback holding a reference
    // to a destroyed component.
    this.subscriptions.push(
      this.resourceManagementService.systemUserEmailAddress.subscribe(
        (email) => (this.currentEmailAddress = email)
      )
    );
    if (this.company) {
      this.loadLocations();
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach((s) => s.unsubscribe());
    super.ngOnDestroy();
  }

  valid(control: string) {
    return this.innerFormGroup.get(control).valid;
  }

  touched(control: string) {
    return this.innerFormGroup.get(control).touched;
  }

  validPN(index: number, control: string) {
    return this.phoneNumbers.at(index).get(control).valid;
  }

  touchedPN(index: number, control: string) {
    return this.phoneNumbers.at(index).get(control).touched;
  }

  validA(index: number, control: string) {
    return this.addresses.at(index).get(control).valid;
  }

  touchedA(index: number, control: string) {
    return this.addresses.at(index).get(control).touched;
  }

  validate(_: FormControl) {
    return this.innerFormGroup.valid
      ? null
      : { generalDetails: { valid: false } };
  }

  // Address
  addAddress() {
    const group = this.formBuilder.group({
      id: [""],
      addressType: [null, Validators.required],
      address1: ["", Validators.required],
      address2: [""],
      city: ["", Validators.required],
      state: [null, Validators.required],
      zip: ["", [Validators.required, Validators.pattern(this.zipCodePattern)]],
      zipExtension: [""],
      deleted: [""],
    });

    this.addresses.push(group);
  }

  removeAddress(event, index, selectedAddress) {
    event.preventDefault();
    if (selectedAddress.value["id"] > 0) {
      selectedAddress.get("deleted").setValue(true);
      selectedAddress.get("addressType").clearValidators();
      selectedAddress.get("address1").clearValidators();
      selectedAddress.get("city").clearValidators();
      selectedAddress.get("state").clearValidators();
      selectedAddress.get("zip").clearValidators();
      selectedAddress.get("zipExtension").clearValidators();
      this.deleteAddress.emit(selectedAddress.value);
    }
    this.addresses.removeAt(index);
  }

  // Phone Number
  addPhoneNumber(phoneType: BusinessPhoneNumberType) {
    const group = this.formBuilder.group({
      id: [null],
      phoneNumber: [null],
      phoneNumberExtension: [""],
      phoneNumberType: [phoneType],
      deleted: [false],
      primary: [false],
    });

    if (phoneType === BusinessPhoneNumberType.WORK) {
      this.workPhoneNumbers.push(group);
    } else if (phoneType === BusinessPhoneNumberType.FAX) {
      this.faxPhoneNumbers.push(group);
    } else if (phoneType === BusinessPhoneNumberType.CELL) {
      this.cellPhoneNumbers.push(group);
    }
  }

  addPhoneNumberForNewResource() {
    let group = this.formBuilder.group({
      id: [null],
      phoneNumber: [null, [Validators.required, Validators.minLength(13)]],
      phoneNumberExtension: [""],
      phoneNumberType: [null, Validators.required],
      deleted: [false],
      primary: [false],
    });
    this.phoneNumbers.push(group);
  }

  // Volunteer
  addVolunteerAttributes() {
    if (!this.innerFormGroup.contains("dateOfBirth")) {
      this.innerFormGroup.addControl(
        "dateOfBirth",
        this.formBuilder.control(null)
      );
    }
    if (!this.innerFormGroup.contains("primaryLanguage")) {
      this.innerFormGroup.addControl(
        "primaryLanguage",
        this.formBuilder.control(null)
      );
    }

    this.volunteerAttributesAdded = true;
  }

  setContactInputs() {
    if (this.contactIsEmployee || this.contactIsVolunteer) {
      if (this.contactIsEmployee) {
        this.addEmployeeAttributes();
      }
      this.addVolunteerAttributes();
    }

    if (!this.contactIsEmployee) {
      this.removeEmployeeAttributes();

      if (!this.contactIsVolunteer) {
        this.removeVolunteerAttributes();
      }
    }
  }

  removeVolunteerAttributes() {
    this.volunteerAttributesAdded = false;
    if (this.innerFormGroup.contains("dateOfBirth")) {
      this.innerFormGroup.removeControl("dateOfBirth");
    }
    if (this.innerFormGroup.contains("primaryLanguage")) {
      this.innerFormGroup.removeControl("primaryLanguage");
    }
  }

  // Employee
  relationship: "No";
  isDisabled: false;

  addEmployeeAttributes() {
    if (!this.innerFormGroup.contains("hireDate")) {
      this.innerFormGroup.addControl("hireDate", this.formBuilder.control(""));
    }
    if (!this.innerFormGroup.contains("employeeAltId")) {
      this.innerFormGroup.addControl(
        "employeeAltId",
        this.formBuilder.control("")
      );
    }
    if (!this.innerFormGroup.contains("driversLicenseNumber")) {
      this.innerFormGroup.addControl(
        "driversLicenseNumber",
        this.formBuilder.control("")
      );
    }
    if (!this.innerFormGroup.contains("driversLicenseExpiration")) {
      this.innerFormGroup.addControl(
        "driversLicenseExpiration",
        this.formBuilder.control("")
      );
    }
    if (!this.innerFormGroup.contains("payrollId")) {
      this.innerFormGroup.addControl("payrollId", this.formBuilder.control(""));
    }
  }

  removeEmployeeAttributes() {
    if (this.innerFormGroup.contains("hireDate")) {
      this.innerFormGroup.removeControl("hireDate");
    }
    if (this.innerFormGroup.contains("employeeAltId")) {
      this.innerFormGroup.removeControl("employeeAltId");
    }
    if (this.innerFormGroup.contains("driversLicenseNumber")) {
      this.innerFormGroup.removeControl("driversLicenseNumber");
    }
    if (this.innerFormGroup.contains("driversLicenseExpiration")) {
      this.innerFormGroup.removeControl("driversLicenseExpiration");
    }
    if (this.innerFormGroup.contains("payrollId")) {
      this.innerFormGroup.removeControl("payrollId");
    }
  }

  filterDeletedControls(controls: AbstractControl[]): AbstractControl[] {
    return controls.filter(
      (c) =>
        c.value["deleted"] == false ||
        c.value["deleted"] == "" ||
        c.value["deleted"] == null
    );
  }

  private containsControl(name: string) {
    return this.innerFormGroup.contains(name);
  }

  private removeControl(name: string) {
    this.innerFormGroup.removeControl(name);
  }

  addAccountSetup() {
    this._contactIsSystemUser = true;
    this.emailAddress.setValidators([
      Validators.pattern(this.emailRegex),
      Validators.maxLength(50),
      Validators.required,
    ]);
    this.emailAddress.updateValueAndValidity();
  }

  removeAccountSetup() {
    this._contactIsSystemUser = false;
    this.emailAddress.setValidators([
      Validators.pattern(this.emailRegex),
      Validators.maxLength(50),
    ]);
    this.emailAddress.updateValueAndValidity();
  }

  updatePrimaryLocation(event: LocationDto) {
    this.removePrimaryLocationOption(event);
    this.otherLocationsSelected = [];
    // OPS-2858 prerequisite fix: clear any previously-flagged primary before setting
    // the new one. This only ever set the incoming option true, relying on every
    // option starting false - so re-selecting could leave TWO primaries. The server
    // then derives contacts.tenant_id from the FIRST primary and
    // UserProfile.PrimaryOrgId from the LAST, and contactLocations is a HashSet there,
    // so which one wins is non-deterministic.
    this.primaryLocations.forEach((location) => {
      location.isPrimaryLocation = false;
    });
    if (event) {
      event.isPrimaryLocation = true;
    }
    this.setLocationsFormValues();
  }

  updateOtherLocation(event: any) {
    this.innerFormGroup.get("otherLocations").setValue(event);
    this.innerFormGroup.get("otherLocations").markAsTouched();
  }

  loadFilters() {
    this.request = {};
    // OPS-2858 prerequisite fix: the picker sent no size, so it silently took the
    // server default of 20 (OPEN_SEARCH_PAGINATION_SIZE). Any company with more
    // than 20 locations has been showing an incomplete list.
    this.request.from = 0;
    this.request.size = LOCATION_FETCH_SIZE;
    this.request.sortBy = {
      name: this.sortOptions.column,
      value: this.sortOptions.direction,
    };
    this.request.entityType = ENTITY_TYPE_LOCATION;

    if (this.isSupportUser) {
      // OPS-2858: the single-company constraint was never a schema or API limit - it
      // is THIS client-supplied filter. Support users hold orgs across companies, so
      // for them the picker asks for every location system-wide.
      // POST /companies/search applies no server-side tenant scoping, so omitting
      // mustFields is all that is required.
      this.request.mustFields = [];
    } else if (this.user.selectedOrgType !== "LOCATION") {
      this.request.mustFields = [
        {
          name: "companyId",
          value: this.company.companyId,
        },
      ];
    } else {
      this.request.mustFields = [
        {
          name: "orgId",
          value: this.user.tenantId.toString(),
        },
      ];
    }
  }

  loadLocations() {
    this.utilityService.increaseSpinnerCount();
    this.loadFilters();

    this.primaryLocations = [];
    this.companyManagementService.getCompanyLocations(this.request).subscribe(
      (response) => {
        if (response && response["hits"] && response["totalHits"] > 0) {
          if (this.user.selectedOrgType !== "LOCATION") {
            this.primaryLocations = (response["hits"] as CompanyLocation[]).map(
              (a) => {
                return {
                  displayCode: a.displayCode,
                  name: a.name,
                  isPrimaryLocation: false,
                  orgId: a.orgId,
                  entityType: a.entityType,
                  // OPS-2858 display-only, for the support-user tree.
                  companyId: (a as any).companyId
                    ? (a as any).companyId.toString()
                    : undefined,
                  isTest: (a as any).isTest,
                  // OPS-2858. Inactive orgs are LOADED and hidden, not filtered out
                  // server-side - "Show Inactive" reveals them without a refetch.
                  isActive: (a as any).isActive !== false,
                  isRcmClient: (a as any).isRcmClient === true,
                } as LocationDto;
              }
            );
            this.primaryLocations.push({
              displayCode: this.user.tenantCode,
              name: this.user.tenantName,
              isPrimaryLocation: false,
              orgId: this.user.tenantId.toString(),
              entityType: ENTITY_TYPE_COMPANY,
            });
            this.otherLocations = this.otherLocations.concat(
              this.primaryLocations
            );
            this.otherLocations = this.otherLocations.filter(
              (l) => l.entityType !== ENTITY_TYPE_COMPANY
            );

            this.setCompanyId();

            // OPS-2858: the tree needs the COMPANY rows too, which is a second call
            // because entityType is mandatory on the search DTO.
            if (this.isSupportUser) {
              this.loadOrgTree(this.primaryLocations);
            }

            if (this.isSupportUser) {
              this.pinPrimaryToCompany();
            }

            if (this.isSupportUser) {
              this.applySupportUserDefaults();
            }
          } else {
            let companyData: Company = {
              companyId: response["hits"][0].companyId,
              name: response["hits"][0].companyName,
            };

            this.primaryLocations = [
              {
                isPrimaryLocation: true,
                displayCode: this.user.tenantCode,
                name: this.user.tenantName,
                orgId: this.user.selectedOrg,
                entityType: this.user.selectedOrgType,
              },
            ];
            let primaryLocationId;
            if (
              this.primaryLocationSelected &&
              this.primaryLocationSelected.id
            ) {
              primaryLocationId = this.primaryLocationSelected.id;
            }
            this.primaryLocationSelected = this.primaryLocations[0];
            this.primaryLocationSelected.id = primaryLocationId
              ? primaryLocationId
              : null;
            this.otherLocations = this.otherLocations.concat(
              this.primaryLocations
            );

            this.setCompanyId(companyData);
          }
        }
        this.utilityService.decreaseSpinnerCount();
      },
      (err) => {
        this.utilityService.decreaseSpinnerCount();
      },
      () => {
        this.setSelectedLocationsWhenEditing();
        this.utilityService.decreaseSpinnerCount();
      }
    );
  }

  // CO-3511 KNOWN TYPO, DELIBERATELY NOT CHANGED: the four setValue() calls in this
  // method and in setPrimaryLocation()/setOtherLocations() pass `emmitEvent: false`.
  // The real option is `emitEvent`, so the misspelling is ignored and valueChanges
  // DOES fire — the opposite of what the author intended. These are the only four
  // occurrences in the whole codebase, so it is a genuine typo rather than a local
  // convention.
  //
  // It is left as-is on purpose: the screen has behaved this way for years, and
  // silencing these events would stop companyId/companyName/location changes
  // propagating to the parent form, which is what checkCanSave() gates Save on.
  // Correcting it is a behaviour change that needs its own ticket and its own
  // regression run, not a drive-by inside a coverage pass.
  private setCompanyId(companyData: Company = null) {
    if (
      companyData &&
      this.user.organizations.filter((o) => o.orgType === "COMPANY").length > 1
    ) {
      this.company = companyData;
    }

    this.innerFormGroup
      .get("companyId")
      .setValue(this.company.companyId, { onlySelf: true, emmitEvent: false });
    this.innerFormGroup
      .get("companyName")
      .setValue(this.company.name, { onlySelf: true, emmitEvent: false });
    this.innerFormGroup.get("companyId").markAsTouched({ onlySelf: true });
    this.innerFormGroup.get("companyName").markAsTouched({ onlySelf: true });
  }

  isSelected(item): boolean {
    return this.otherLocationsSelected.indexOf(item) > -1;
  }

  toggleItem(item): void {
    if (!this.isSelected(item)) {
      this.otherLocationsSelected.push(item);
    }
  }

  onItemSelect(event): void {
    this.innerFormGroup
      .get("otherLocations")
      .setValue(this.otherLocationsSelected);
    this.innerFormGroup.get("otherLocations").markAsTouched({ onlySelf: true });
  }

  clearSelection(): void {
    this.otherLocationsSelected = [];
  }

  toggleSelectAll(): void {
    if (this.allSelected()) {
      this.otherLocationsSelected = [];
    } else {
      this.otherLocationsSelected = [...this.otherLocations];
    }
  }

  allSelected(): boolean {
    return this.otherLocationsSelected.length === this.otherLocations.length;
  }

  setLocationsSelected(
    mainLocation: LocationDto,
    otherLocations: LocationDto[]
  ) {
    this.primaryLocationSelected = mainLocation;
    this.otherLocationsSelected = otherLocations;
    this.setLocationsFormValues();
  }

  private setSelectedLocationsWhenEditing() {
    if (!this.isNew && this.primaryLocationSelected) {
      // OPS-2858 prerequisite fix: matched on displayCode, which is unique only
      // WITHIN a company. Support users hold orgs across companies, where
      // abbreviations collide (two "MAIN"s), so this rebound the grant to the
      // wrong org. orgId is globally unique.
      let primaryLocation = this.primaryLocations.find(
        (a) => a.orgId === this.primaryLocationSelected.orgId
      );
      // CO-3511: `primaryLocation` was dereferenced unguarded, so opening a resource
      // whose primary location had since been removed from the company threw — and
      // the "Must Select A Company" fallback below was unreachable because the crash
      // happened while evaluating its condition. The block immediately after this one
      // already guards the identical lookup for other-locations; this matches it.
      if (primaryLocation) {
        this.primaryLocationSelected.name = primaryLocation.name
          ? primaryLocation.name
          : "Must Select A Company";
        this.primaryLocationSelected.entityType = primaryLocation.entityType;
        this.primaryLocationSelected.orgId = primaryLocation.orgId;
      } else {
        this.primaryLocationSelected.name = "Must Select A Company";
      }

      let otherLocationsFiltered: LocationDto[] = [];
      //remove the locations that were saved in RM, but they don't longer exist in the Company
      this.otherLocationsSelected.forEach((a) => {
        let otherLocation = this.otherLocations.find(
          (b) => b.orgId === a.orgId
        );
        if (otherLocation) {
          otherLocationsFiltered.push(otherLocation);
        }
      });

      this.otherLocationsSelected = otherLocationsFiltered;

      this.otherLocationsSelected.forEach((a) => {
        let otherLocation = this.otherLocations.find(
          (b) => b.orgId === a.orgId
        );
        if (otherLocation) {
          a.name = otherLocation.name;
          a.entityType = otherLocation.entityType;
          a.orgId = otherLocation.orgId;
        }
      });

      this.removePrimaryLocationOption(this.primaryLocationSelected);
      this.setLocationsFormValues();
      this.cleanNameInputs();
    } else if (this.isNew && this.primaryLocationSelected) {
      this.setPrimaryLocation();
    }
  }

  private removePrimaryLocationOption(primaryLocation: LocationDto) {
    if (primaryLocation) {
      this.otherLocations = this.primaryLocations.filter(
        (a) =>
          a.displayCode !== primaryLocation.displayCode &&
          a.entityType !== ENTITY_TYPE_COMPANY
      );
    } else {
      this.otherLocations = this.primaryLocations.filter(
        (l) => l.entityType !== ENTITY_TYPE_COMPANY
      );
    }
  }

  private setPrimaryLocation() {
    this.innerFormGroup.get("location").setValue(this.primaryLocationSelected, {
      onlySelf: true,
      emmitEvent: false,
    });
    this.innerFormGroup.get("location").markAsTouched({ onlySelf: true });
    this.innerFormGroup.updateValueAndValidity();
  }

  private setOtherLocations() {
    this.innerFormGroup
      .get("otherLocations")
      .setValue(this.otherLocationsSelected, {
        onlySelf: true,
        emmitEvent: false,
      });

    this.innerFormGroup.get("otherLocations").markAsTouched({ onlySelf: true });
    this.innerFormGroup.updateValueAndValidity();
  }

  private setLocationsFormValues() {
    this.setPrimaryLocation();
    this.setOtherLocations();
  }

  private mapLookups(rmLookups: ResourceManagementLookups) {
    this.resourceTypeOptions = rmLookups["ContactType"]
      .map((item) => {
        return {
          display: item.description,
          value: item.id,
        };
      })
      .filter((item) => {
        let backOffice = item.display === "Back Office";
        let communityResource = item.display === "Community Resource";
        let patientContact = item.display === "Patient Contact";

        return !(backOffice || communityResource || patientContact);
      });
    this.professionalRelationOptions = rmLookups["ProfessionalRelationType"]
      .filter((item) => item.description !== "Auditor")
      .map((item) => {
        return {
          display: item.description,
          value: item.id,
        };
      });

    this.statusOptions = rmLookups["ResourceStatus"].map((item) => {
      return {
        display: item.description,
        value: item.id,
      };
    });

    this.allAddressTypeOptions = rmLookups["AddressType"].map((item) => {
      return {
        display: item.description,
        value: item.id,
      };
    });
    this.genderOptions = rmLookups["Gender"].map((item) => {
      return {
        display: item.description,
        value: item.id,
      };
    });
    this.allPhoneNumberOptions = rmLookups["PhoneNumberType"].map((item) => {
      return {
        display: item.description,
        value: item.id,
      };
    });
    this.languageOptions = rmLookups["Language"].map((item) => {
      return {
        display: item.description,
        value: item.id,
      };
    });
    this.suffixOptions = rmLookups["Suffix"].map((item) => {
      return {
        display: item.description,
        value: item.id,
      };
    });
    this.phoneNumberOptions = this.allPhoneNumberOptions;
    this.addressTypeOptions = this.allAddressTypeOptions;
    this.setDefaultResourceStatus();
  }

  private setDefaultResourceStatus() {
    let resource = this.statusOptions.find((x) => x.display === "Active");
    let defaultResourceStatus = resource ? resource.value : null;
    this.innerFormGroup.get("resourceStatus").setValue(defaultResourceStatus);
    this.innerFormGroup.get("resourceStatus").markAsTouched({ onlySelf: true });
  }

  checkCognitoUserPermissions() {
    this.hasAccessToAccountSetup = this.accessCntrl.hasAccess(
      this._permissionsAccess,
      Workforce.CompanyWorkforceAll
    );
  }

  validateTextOnlyInput(event: any, controlName: string): void {
    let value = event.target.value;
    let cleanedValue = value.replace(/[^\p{L}\p{M}\s'¨´-]/gu, "");
    event.target.value = cleanedValue;
    this.innerFormGroup
      .get(controlName)
      .setValue(cleanedValue, { emitEvent: false });
  }

  cleanNameInputs(): void {
    const formControls = ["firstName", "middleInitial", "lastName"];

    formControls.forEach((controlName) => {
      const control = this.innerFormGroup.get(controlName);
      if (control && control.value) {
        const cleanedValue = control.value.replace(/[^\p{L}\p{M}\s'¨´-]/gu, "");
        control.setValue(cleanedValue, { emitEvent: false });
        control.markAsDirty();
        control.markAsTouched();
        control.updateValueAndValidity();
      }
    });
  }

  ngAfterViewInit(): void {
    if (this.isNew) {
      this.addPhoneNumberForNewResource();
    }
    // OPS-2858: also here, not just from the loadLocations subscribe. The phone array
    // for a NEW resource is seeded on the line above, and the location fetch is async,
    // so either hook can win. applySupportUserDefaults is idempotent - every setter
    // guards on the field already being populated.
    this.applySupportUserDefaults();
  }

  /**
   * OPS-2858. Fetches every COMPANY and joins the already-loaded locations onto them,
   * producing the checkbox tree for the Support Users picker.
   *
   * TWO calls are required: entityType is mandatory on the search DTO, and the
   * existing picker asks only for LOCATION. POST /companies/search applies no
   * server-side tenant scoping, so neither call needs a company filter.
   *
   * Sort: company name ASC, then isTest ASC, then location name ASC. false sorts
   * before true, so each company is followed by its live sites and then its
   * training sites.
   */
  private loadOrgTree(locations: LocationDto[]) {
    const companyRequest: any = {
      entityType: ENTITY_TYPE_COMPANY,
      from: 0,
      size: COMPANY_FETCH_SIZE,
      sortBy: { name: "name", value: "asc" },
      mustFields: [],
    };

    this.companyManagementService.getCompanyLocations(companyRequest).subscribe(
      (response) => {
        const hits =
          response && response["hits"] ? (response["hits"] as any[]) : [];

        const byCompany: { [companyId: string]: LocationDto[] } = {};
        locations.forEach((location) => {
          const key = location.companyId ? location.companyId.toString() : "";
          if (!byCompany[key]) {
            byCompany[key] = [];
          }
          byCompany[key].push(location);
        });

        this.orgTree = hits
          .map((company) => {
            const companyId = company.companyId
              ? company.companyId.toString()
              : company.orgId;
            const children = (byCompany[companyId] || []).sort((a, b) =>
              this.compareLocations(a, b)
            );
            // OPS-2858. orgSubType lives on the COMPANY and locations inherit it.
            // Stamping it onto each child here is what lets the group criteria treat
            // companies and locations uniformly instead of every call site having to
            // walk back up to the parent.
            children.forEach(
              (child) => (child.orgSubType = company.orgSubType)
            );

            return {
              org: {
                displayCode: company.displayCode,
                name: company.name,
                isPrimaryLocation: false,
                orgId: company.orgId,
                entityType: ENTITY_TYPE_COMPANY,
                // Absent means UNCLASSIFIED. Do not default it - the sub-type is
                // maintained by hand and most companies will not have one yet.
                orgSubType: company.orgSubType,
                companyId: companyId,
                isActive: company.isActive !== false,
                isRcmClient: company.isRcmClient === true,
              } as LocationDto,
              locations: children,
            };
          })
          .sort((a, b) => this.compareOrgNodes(a, b));

        // Build the render list once, here - not in a getter. See visibleOrgTree.
        this.refreshVisibleOrgTree();

        this.utilityService.decreaseSpinnerCount();
      },
      () => this.utilityService.decreaseSpinnerCount()
    );
  }

  /**
   * OPS-2858. Everything a support user gets pre-set on General Details.
   *
   * Called once the form exists and only when isSupportUser is true, so the other
   * three contact types are completely untouched.
   */
  private applySupportUserDefaults() {
    if (!this.isSupportUser || !this.innerFormGroup) {
      return;
    }

    // --- FIXED, not editable (the template disables these controls) ------------
    // A support user is always a Non-Provider Clinician who is an Employee and has
    // a login. Type 4 is load-bearing: it is what the Support Users tab filters on,
    // and it keeps NPI/DEA from ever being required (addProviderControls only fires
    // for PROVIDER).
    this.innerFormGroup
      .get("resourceType")
      .setValue(SUPPORT_USER_RESOURCE_TYPE, { emitEvent: false });
    this.innerFormGroup
      .get("professionalRelation")
      .setValue(SUPPORT_USER_PROFESSIONAL_RELATION, { emitEvent: false });
    this.innerFormGroup.get("systemUser").setValue(true);

    // --- DEFAULTED but editable -----------------------------------------------
    if (!this.innerFormGroup.get("resourceStatus").value) {
      this.innerFormGroup
        .get("resourceStatus")
        .setValue(RESOURCE_STATUS_ACTIVE, { emitEvent: false });
    }

    this.applySupportUserPhoneDefault();
    this.applySupportUserAddressDefault();
  }

  /**
   * The work phone is the one field here that MUST be populated. See
   * SUPPORT_USER_DEFAULT_WORK_PHONE: an empty list is a 500, not a validation error.
   */
  private applySupportUserPhoneDefault() {
    const workPhones = this.innerFormGroup.get("workPhoneNumbers") as FormArray;
    if (!workPhones || workPhones.length === 0) {
      return;
    }
    const first = workPhones.at(0);
    if (first && first.get("phoneNumber") && !first.get("phoneNumber").value) {
      first.get("phoneNumber").setValue(SUPPORT_USER_DEFAULT_WORK_PHONE);
    }
  }

  /**
   * The address is NOT required by the backend: no bean validation on
   * CompanyResourceDto, and every address dereference in MappingUtilities guards
   * with CollectionUtils.isEmpty. So the validators come off, and the Curantis
   * office is filled in as a convenience rather than an obligation.
   */
  private applySupportUserAddressDefault() {
    const addresses = this.addresses;
    if (!addresses || addresses.length === 0) {
      return;
    }
    const first = addresses.at(0);
    if (!first) {
      return;
    }

    ["addressType", "address1", "city", "state", "zip"].forEach((name) => {
      const control = first.get(name);
      if (control) {
        control.clearValidators();
        control.updateValueAndValidity({ emitEvent: false });
      }
    });

    if (!first.get("address1").value) {
      first.get("addressType").setValue(ADDRESS_TYPE_OFFICE);
      first.get("address1").setValue(SUPPORT_USER_DEFAULT_ADDRESS.address1);
      first.get("city").setValue(SUPPORT_USER_DEFAULT_ADDRESS.city);
      first.get("state").setValue(SUPPORT_USER_DEFAULT_ADDRESS.state);
      first.get("zip").setValue(SUPPORT_USER_DEFAULT_ADDRESS.zip);
    }
  }

  /**
   * OPS-2858. A support user's primary org is the company they were created in, and
   * is not user-editable. The server derives contacts.tenant_id from whichever
   * contactLocation carries isPrimaryLocation, so letting this drift would re-tenant
   * the contact and orphan it from the admin who manages it.
   *
   * loadLocations already appends the current COMPANY as an option; this selects it.
   */
  private pinPrimaryToCompany() {
    const companyOption = this.primaryLocations.find(
      (l) => l.entityType === ENTITY_TYPE_COMPANY
    );
    if (!companyOption) {
      return;
    }
    this.primaryLocations.forEach((l) => (l.isPrimaryLocation = false));
    companyOption.isPrimaryLocation = true;
    this.primaryLocationSelected = companyOption;
    this.setLocationsFormValues();
  }

  // ---- OPS-2858 org picker: view state -------------------------------------

  /** Active sub-type facets. Empty means no sub-type filtering. */
  subTypeFilters: string[] = [];

  /**
   * OPS-2858 quick-select. Five one-click grants above the tree.
   *
   * THE MODEL, because it is not obvious from the boxes alone:
   *
   *  - Checking a group SELECTS every ACTIVE org matching its criteria.
   *  - Unchecking a group DESELECTS those orgs, EXCEPT any that also match a group
   *    that is still checked. That single rule produces both behaviours Maggie
   *    specified: unchecking "All Clients" keeps an RCM client selected while
   *    "All RCM Clients" is still on, and unchecking "All RCM Clients" keeps
   *    everything because "All Clients" is a superset.
   *  - "All" is a SUPERSET of the four, not their union: it also covers active
   *    SALES_DEMO and UNCLASSIFIED orgs, which no other box matches. It is therefore
   *    MANUAL-ONLY in the on direction - checking all four does NOT check it, because
   *    that would silently grant more than was ticked. Checking All still auto-checks
   *    the four, and unchecking any of them still clears All.
   *  - Every group is ACTIVE-ONLY. Inactive orgs are never touched by these boxes -
   *    they are manual-only, by design (see showInactive).
   */
  quickSelect: { [group: string]: boolean } = {
    all: false,
    clients: false,
    rcmClients: false,
    internalTraining: false,
    internalTest: false,
  };

  /**
   * OPS-2858. Inactive orgs are LOADED but hidden. Revealing them is deliberate
   * friction: granting access to a deactivated org is almost always a mistake, so it
   * takes an explicit action, the rows render faded, and no bulk control can select
   * them. Hiding them again DESELECTS any that were picked, so a stray click cannot
   * ride along to Save unnoticed.
   */
  showInactive: boolean = false;

  /** The order the boxes render in, and the order groups are evaluated in. */
  readonly quickSelectGroups: { key: string; label: string }[] = [
    { key: "all", label: "All" },
    { key: "clients", label: "All Clients" },
    { key: "rcmClients", label: "All RCM Clients" },
    { key: "internalTraining", label: "All Internal Training" },
    { key: "internalTest", label: "All Internal Test" },
  ];

  /** Every group except All, which is handled separately in both directions. */
  private readonly subGroups = [
    "clients",
    "rcmClients",
    "internalTraining",
    "internalTest",
  ];

  /**
   * Sub-types present in the data, for the facet buttons. Derived rather than
   * hardcoded so the RCM flag - and anything else added later - needs no new UI.
   */
  get availableSubTypes(): string[] {
    const seen: string[] = [];
    this.orgTree.forEach((node) => {
      const subType = parseOrgSubType(node.org.orgSubType);
      if (seen.indexOf(subType) === -1) {
        seen.push(subType);
      }
    });
    // Canonical order, not alphabetical: UNCLASSIFIED leads, because while the ~50
    // companies are being classified by hand the useful question is "what is left?".
    return ORG_SUB_TYPES.filter((code) => seen.indexOf(code) > -1);
  }

  toggleSubTypeFilter(subType: string) {
    const at = this.subTypeFilters.indexOf(subType);
    if (at > -1) {
      this.subTypeFilters.splice(at, 1);
    } else {
      this.subTypeFilters.push(subType);
    }
    this.refreshVisibleOrgTree();
  }

  isSubTypeFilterActive(subType: string): boolean {
    return this.subTypeFilters.indexOf(subType) > -1;
  }

  /** UNCLASSIFIED, not a default. Most companies will not carry a sub-type yet. */
  subTypeLabel(org: LocationDto): string {
    return orgSubTypeLabel(org ? org.orgSubType : null);
  }

  /** The facet buttons show labels; the filter list holds codes. */
  subTypeFacetLabel(subType: string): string {
    return orgSubTypeLabel(subType);
  }

  /**
   * The tree as rendered.
   *
   * A FIELD, not a getter, and this is load-bearing. It was a getter that rebuilt
   * `{org, locations}` wrappers on every call; `*ngFor` diffs by identity, so every
   * change-detection cycle tore down and recreated EVERY row. Clicking a checkbox
   * destroyed the element before its `change` event could fire, so individual
   * select/deselect silently did nothing while `[Select all shown]` — which sits
   * OUTSIDE the *ngFor and therefore has a stable DOM node — worked fine.
   *
   * Recompute explicitly via refreshVisibleOrgTree() when the inputs change.
   */
  visibleOrgTree: { org: LocationDto; locations: LocationDto[] }[] = [];

  /** Identity for *ngFor, so a recompute patches rows instead of recreating them. */
  trackByOrgId(_index: number, item: any): string {
    return item && item.org
      ? item.org.orgId
      : item
      ? item.orgId
      : String(_index);
  }

  private refreshVisibleOrgTree() {
    this.visibleOrgTree = this.orgTree
      .map((node) => {
        // Compare CODES. subTypeLabel returns a display string ("Sales/Demo"),
        // which is not what subTypeFilters holds.
        const subTypeOk =
          this.subTypeFilters.length === 0 ||
          this.isSubTypeFilterActive(parseOrgSubType(node.org.orgSubType));
        if (!subTypeOk) {
          return null;
        }

        // OPS-2858. Inactive orgs are loaded but hidden until "Show Inactive".
        // Filtered per-ROW, not per-company: an active company can have inactive
        // locations and vice versa, and hiding a whole company because it is
        // inactive would hide live locations underneath it.
        if (this.showInactive) {
          return node;
        }
        if (this.isOrgInactive(node.org)) {
          return null;
        }
        const activeLocations = node.locations.filter(
          (location) => !this.isOrgInactive(location)
        );
        return { org: node.org, locations: activeLocations };
      })
      .filter((node) => node !== null);
  }

  /**
   * OPS-2858 company ordering: sub-type, then company id.
   *
   * Sub-type leads so the tree groups into "clients / demo / internal / not yet
   * classified" bands, which is how the quick-select boxes carve it up. Within a
   * band, company id rather than name - it is what appears on the row and what
   * Karen works from when reading an access ticket.
   */
  private compareOrgNodes(
    a: { org: LocationDto },
    b: { org: LocationDto }
  ): number {
    const subTypeRank =
      ORG_SUB_TYPES.indexOf(parseOrgSubType(a.org.orgSubType)) -
      ORG_SUB_TYPES.indexOf(parseOrgSubType(b.org.orgSubType));
    if (subTypeRank !== 0) {
      return subTypeRank;
    }
    return (a.org.companyId || "").localeCompare(b.org.companyId || "");
  }

  /**
   * Selection state for ONE org - company or location alike.
   *
   * A COMPANY is a grantable org in its own right: iam-functions matches
   * OrgRole.OrgID == tenantId with NO hierarchy. So this is deliberately not a
   * tri-state parent: ticking a company grants the COMPANY org, and says nothing
   * about its locations.
   */
  isOrgSelected(org: LocationDto): boolean {
    if (!org) {
      return false;
    }
    if (
      this.primaryLocationSelected &&
      this.primaryLocationSelected.orgId === org.orgId
    ) {
      return true;
    }
    return this.otherLocationsSelected.some((s) => s.orgId === org.orgId);
  }

  /** The pinned primary cannot be unticked - it is the user's home org. */
  isOrgLocked(org: LocationDto): boolean {
    return (
      !!this.primaryLocationSelected &&
      this.primaryLocationSelected.orgId === org.orgId
    );
  }

  toggleOrg(org: LocationDto) {
    if (!this.isEditMode || this.isOrgLocked(org)) {
      return;
    }
    const at = this.otherLocationsSelected.findIndex(
      (s) => s.orgId === org.orgId
    );
    if (at > -1) {
      this.otherLocationsSelected.splice(at, 1);
    } else {
      this.otherLocationsSelected.push(org);
    }
    this.setOtherLocations();
  }

  /** Bulk over what is CURRENTLY SHOWN, so it composes with the filters. */
  selectAllShown() {
    if (!this.isEditMode) {
      return;
    }
    this.visibleOrgTree.forEach((node) => {
      [node.org].concat(node.locations).forEach((org) => {
        if (!this.isOrgSelected(org) && !this.isOrgLocked(org)) {
          this.otherLocationsSelected.push(org);
        }
      });
    });
    this.setOtherLocations();
  }

  clearShown() {
    if (!this.isEditMode) {
      return;
    }
    const shown: string[] = [];
    this.visibleOrgTree.forEach((node) => {
      [node.org].concat(node.locations).forEach((org) => {
        if (!this.isOrgLocked(org)) {
          shown.push(org.orgId);
        }
      });
    });
    this.otherLocationsSelected = this.otherLocationsSelected.filter(
      (s) => shown.indexOf(s.orgId) === -1
    );
    this.setOtherLocations();
  }

  // ---- OPS-2858 quick-select ------------------------------------------------

  /**
   * Whether one org matches a group's criteria.
   *
   * ACTIVE-ONLY across the board. Maggie's wording varied per box ("all CLIENT
   * companies" vs "all active INTERNAL_TRAINING companies"), but the Show Inactive
   * rule settles it: no bulk control may touch an inactive org.
   *
   * orgSubType is stamped onto locations from their parent company at load time, so
   * a company and its locations answer identically here.
   */
  private matchesGroup(group: string, org: LocationDto): boolean {
    if (!org || org.isActive === false) {
      return false;
    }
    const subType = parseOrgSubType(org.orgSubType);
    switch (group) {
      case "all":
        return true;
      case "clients":
        return subType === OrgSubType.CLIENT;
      case "rcmClients":
        return subType === OrgSubType.CLIENT && org.isRcmClient === true;
      case "internalTraining":
        return subType === OrgSubType.INTERNAL_TRAINING;
      case "internalTest":
        return subType === OrgSubType.INTERNAL_TEST;
      default:
        return false;
    }
  }

  /** Every org in the tree, companies and locations alike, in render order. */
  private allOrgs(): LocationDto[] {
    const orgs: LocationDto[] = [];
    this.orgTree.forEach((node) => {
      orgs.push(node.org);
      node.locations.forEach((location) => orgs.push(location));
    });
    return orgs;
  }

  isQuickSelectActive(group: string): boolean {
    return this.quickSelect[group] === true;
  }

  toggleQuickSelect(group: string) {
    if (!this.isEditMode) {
      return;
    }

    const turningOn = !this.quickSelect[group];

    if (group === "all") {
      // All drives the other four in BOTH directions. Leaving them checked while
      // All is off would make unchecking All a no-op, since their union is a subset.
      this.quickSelect["all"] = turningOn;
      this.subGroups.forEach((key) => (this.quickSelect[key] = turningOn));
      if (turningOn) {
        this.selectMatching("all");
      } else {
        this.deselectMatching("all");
      }
      this.setOtherLocations();
      return;
    }

    this.quickSelect[group] = turningOn;

    if (turningOn) {
      this.selectMatching(group);
      // NOTE: checking all four does NOT auto-check All. It used to, which quietly
      // pulled in active SALES_DEMO and UNCLASSIFIED orgs, because All is a SUPERSET
      // of the four rather than their union. Karen's common cases are "All Clients"
      // and "All RCM Clients"; silently widening a grant beyond what she ticked is
      // the wrong trade for saving one click. All is manual-only.
    } else {
      // "Unchecking any checkbox will deselect All if it's selected." Note this does
      // NOT clear orgs that only All covered - they do not match this group's
      // criteria, so the rule below leaves them alone.
      this.quickSelect["all"] = false;
      this.deselectMatching(group);
    }

    this.setOtherLocations();
  }

  private selectMatching(group: string) {
    this.allOrgs().forEach((org) => {
      if (
        this.matchesGroup(group, org) &&
        !this.isOrgSelected(org) &&
        !this.isOrgLocked(org)
      ) {
        this.otherLocationsSelected.push(org);
      }
    });
  }

  /**
   * Deselect everything matching this group EXCEPT what a still-checked group also
   * covers. This one rule is the whole of Maggie's retention spec.
   */
  private deselectMatching(group: string) {
    const stillChecked = Object.keys(this.quickSelect).filter(
      (key) => key !== group && this.quickSelect[key]
    );

    const doomed: string[] = [];
    this.allOrgs().forEach((org) => {
      if (!this.matchesGroup(group, org) || this.isOrgLocked(org)) {
        return;
      }
      const retained = stillChecked.some((key) => this.matchesGroup(key, org));
      if (!retained) {
        doomed.push(org.orgId);
      }
    });

    this.otherLocationsSelected = this.otherLocationsSelected.filter(
      (selected) => doomed.indexOf(selected.orgId) === -1
    );
  }

  // ---- OPS-2858 inactive orgs -----------------------------------------------

  toggleShowInactive() {
    if (!this.isEditMode) {
      return;
    }
    this.showInactive = !this.showInactive;

    if (!this.showInactive) {
      // Hiding them deselects them. Otherwise a row the user ticked and then hid
      // would still be granted on Save, invisibly.
      this.otherLocationsSelected = this.otherLocationsSelected.filter(
        (selected) => selected.isActive !== false || this.isOrgLocked(selected)
      );
      this.setOtherLocations();
    }

    this.refreshVisibleOrgTree();
  }

  /** Inactive rows render faded, so it is obvious they are not ordinary choices. */
  isOrgInactive(org: LocationDto): boolean {
    return !!org && org.isActive === false;
  }

  get selectedCompanyCount(): number {
    return this.otherLocationsSelected.filter(
      (s) => s.entityType === ENTITY_TYPE_COMPANY
    ).length;
  }

  get selectedLocationCount(): number {
    return this.otherLocationsSelected.filter(
      (s) => s.entityType !== ENTITY_TYPE_COMPANY
    ).length;
  }

  /** isTest ASC (live before training), then name ASC. */
  /**
   * OPS-2858 location ordering within a company: active first, then live before
   * training, then name.
   *
   * Active leads because inactive rows are hidden by default and, when revealed,
   * are manual-only - they belong at the bottom of the company, out of the way of
   * the rows the bulk controls act on.
   */
  private compareLocations(a: LocationDto, b: LocationDto): number {
    const aInactive = a.isActive === false ? 1 : 0;
    const bInactive = b.isActive === false ? 1 : 0;
    if (aInactive !== bInactive) {
      return aInactive - bInactive;
    }
    const aTraining = a.isTest ? 1 : 0;
    const bTraining = b.isTest ? 1 : 0;
    if (aTraining !== bTraining) {
      return aTraining - bTraining;
    }
    return (a.name || "").localeCompare(b.name || "");
  }
}



// WEBPACK FOOTER //
// ./src/components/system-admin/workforce/workforce-general-contact/workforce-general-contact.ts